import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { Trophy, Zap, Flame, BookOpen, Brain, Medal, ChevronUp, ChevronDown } from "lucide-react";

const SORT_OPTIONS = [
  { key: "xp", label: "XP", icon: Zap },
  { key: "papers", label: "Papers", icon: BookOpen },
  { key: "streak", label: "Streak", icon: Flame },
  { key: "pages", label: "Pages", icon: Brain },
];

const RANK_STYLES = {
  1: "bg-neo-yellow border-neo-yellow",
  2: "bg-gray-200 dark:bg-gray-600 border-gray-400",
  3: "bg-orange-200 dark:bg-orange-800 border-orange-400",
};

export default function Leaderboard({ api }) {
  const { user } = useAuth();
  const [leaderboard, setLeaderboard] = useState([]);
  const [sortBy, setSortBy] = useState("xp");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchLeaderboard = async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${api}/leaderboard?sort_by=${sortBy}`, { withCredentials: true });
        setLeaderboard(res.data);
      } catch {
        toast.error("Failed to load leaderboard");
      } finally {
        setLoading(false);
      }
    };
    fetchLeaderboard();
  }, [api, sortBy]);

  const currentUserId = user?.user_id;

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]" data-testid="leaderboard-loading">
        <div className="neo-card dark:bg-[#1E1E1E] dark:border-[#444] text-center p-8">
          <div className="w-12 h-12 border-4 border-neo-yellow border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="font-heading font-bold text-lg dark:text-white">Loading leaderboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-3xl mx-auto" data-testid="leaderboard-page">
      <div className="text-center">
        <h1 className="font-heading font-extrabold text-4xl sm:text-5xl tracking-tight dark:text-white">
          Leaderboard
        </h1>
        <p className="text-neo-text-secondary dark:text-gray-400 mt-2">See how you stack up against other readers</p>
      </div>

      {/* Sort Tabs */}
      <div className="flex justify-center">
        <div className="inline-flex border-2 border-neo-border dark:border-[#444] rounded-lg overflow-hidden">
          {SORT_OPTIONS.map((opt) => {
            const Icon = opt.icon;
            return (
              <button
                key={opt.key}
                onClick={() => setSortBy(opt.key)}
                className={`px-4 py-2 text-sm font-heading font-bold flex items-center gap-1 transition-all border-r-2 border-neo-border dark:border-[#444] last:border-r-0 ${
                  sortBy === opt.key ? "bg-neo-yellow text-neo-text" : "bg-white dark:bg-[#2A2A2A] dark:text-white hover:bg-gray-50 dark:hover:bg-[#333]"
                }`}
                data-testid={`sort-${opt.key}`}
              >
                <Icon className="w-4 h-4" />
                <span className="hidden sm:inline">{opt.label}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Top 3 Podium */}
      {leaderboard.length >= 3 && (
        <div className="grid grid-cols-3 gap-3 items-end" data-testid="podium">
          {[1, 0, 2].map((idx) => {
            const entry = leaderboard[idx];
            if (!entry) return null;
            const isMe = entry.user_id === currentUserId;
            const heights = { 0: "pt-4", 1: "pt-0", 2: "pt-8" };
            return (
              <div key={entry.user_id} className={`${heights[idx]} text-center`}>
                <div className={`neo-card dark:bg-[#1E1E1E] dark:border-[#444] dark:shadow-[4px_4px_0px_0px_#444] ${isMe ? "border-neo-yellow border-[3px]" : ""}`}>
                  <div className={`w-12 h-12 rounded-full border-2 border-neo-border dark:border-[#444] shadow-[2px_2px_0px_0px_#1A1A1A] flex items-center justify-center mx-auto mb-2 ${RANK_STYLES[entry.rank] || "bg-white dark:bg-[#2A2A2A]"}`}>
                    {entry.rank <= 3 ? (
                      <Medal className="w-6 h-6 text-neo-text" />
                    ) : (
                      <span className="font-heading font-extrabold">{entry.rank}</span>
                    )}
                  </div>
                  <p className="font-heading font-bold text-sm truncate dark:text-white">{entry.name}</p>
                  <p className="font-heading font-extrabold text-xl dark:text-white mt-1">
                    {sortBy === "xp" && `${entry.total_xp} XP`}
                    {sortBy === "papers" && `${entry.papers_completed}`}
                    {sortBy === "streak" && `${entry.current_streak}d`}
                    {sortBy === "pages" && `${entry.total_pages_read}`}
                  </p>
                  <p className="text-xs text-neo-text-secondary dark:text-gray-400">Lv {entry.level}</p>
                  {isMe && <span className="neo-badge text-xs bg-neo-yellow mt-2">You</span>}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Full Rankings */}
      <div className="space-y-2" data-testid="rankings-list">
        {leaderboard.map((entry) => {
          const isMe = entry.user_id === currentUserId;
          return (
            <div
              key={entry.user_id}
              className={`neo-card dark:bg-[#1E1E1E] dark:border-[#444] dark:shadow-[4px_4px_0px_0px_#444] flex items-center gap-4 py-3 ${
                isMe ? "border-neo-yellow border-[3px]" : ""
              }`}
              data-testid={`rank-${entry.rank}`}
            >
              <div className={`w-10 h-10 rounded-full border-2 border-neo-border dark:border-[#444] flex items-center justify-center flex-shrink-0 font-heading font-extrabold ${
                RANK_STYLES[entry.rank] || "bg-white dark:bg-[#2A2A2A] dark:text-white"
              }`}>
                {entry.rank}
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-heading font-bold truncate dark:text-white">
                  {entry.name} {isMe && <span className="text-neo-yellow text-xs">(You)</span>}
                </p>
                <div className="flex items-center gap-3 text-xs text-neo-text-secondary dark:text-gray-400 mt-0.5">
                  <span className="flex items-center gap-1"><Trophy className="w-3 h-3" /> Lv {entry.level}</span>
                  <span className="flex items-center gap-1"><BookOpen className="w-3 h-3" /> {entry.papers_completed} papers</span>
                  <span className="flex items-center gap-1"><Flame className="w-3 h-3" /> {entry.current_streak}d streak</span>
                </div>
              </div>
              <div className="text-right flex-shrink-0">
                <p className="font-heading font-extrabold text-lg dark:text-white">
                  {sortBy === "xp" && `${entry.total_xp}`}
                  {sortBy === "papers" && `${entry.papers_completed}`}
                  {sortBy === "streak" && `${entry.current_streak}`}
                  {sortBy === "pages" && `${entry.total_pages_read}`}
                </p>
                <p className="text-xs text-neo-text-secondary dark:text-gray-400">
                  {sortBy === "xp" && "XP"}
                  {sortBy === "papers" && "papers"}
                  {sortBy === "streak" && "days"}
                  {sortBy === "pages" && "pages"}
                </p>
              </div>
            </div>
          );
        })}
      </div>

      {leaderboard.length === 0 && (
        <div className="neo-card dark:bg-[#1E1E1E] dark:border-[#444] text-center py-12" data-testid="empty-leaderboard">
          <Trophy className="w-16 h-16 text-neo-yellow mx-auto mb-4" />
          <h2 className="font-heading font-extrabold text-2xl dark:text-white">No Rankings Yet</h2>
          <p className="text-neo-text-secondary dark:text-gray-400 mt-2">Start reading to appear on the leaderboard!</p>
        </div>
      )}
    </div>
  );
}
